import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Play, Pause, Volume2, VolumeX, Maximize, SkipBack, SkipForward, Heart, Share2 } from 'lucide-react';

interface DramaScript {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  duration: number;
  genre: string;
  tags: string[];
  script: {
    scenes: Scene[];
  };
  createdAt: string;
  views: number;
  likes: number;
}

interface Scene {
  id: string;
  title: string;
  duration: number;
  setting: string;
  dialogue: Dialogue[];
}

interface Dialogue {
  character: string;
  text: string;
  emotion: string;
  choices?: Choice[];
}

interface Choice {
  id: string;
  text: string;
  nextScene?: string;
  consequence: string;
}

// Mock数据
const mockDramas: DramaScript[] = [
  {
    id: '1',
    title: '时间胶囊',
    description: '一个关于时间旅行者试图改变历史的故事',
    thumbnail: '/api/placeholder/800/450',
    duration: 180,
    genre: '科幻',
    tags: ['时间旅行', '爱情', '悬疑'],
    script: {
      scenes: [
        {
          id: 'scene1',
          title: '发现时间胶囊',
          duration: 60,
          setting: '古老的阁楼，尘土飞扬',
          dialogue: [
            {
              character: '艾米',
              text: '这是什么？一个看起来很古老的盒子...',
              emotion: '好奇'
            },
            {
              character: '爷爷的声音',
              text: '孩子，有些秘密，一旦揭开就无法回头...',
              emotion: '神秘'
            }
          ]
        },
        {
          id: 'scene2',
          title: '第一次穿越',
          duration: 60,
          setting: '1980年代的街道',
          dialogue: [
            {
              character: '艾米',
              text: '天啊，我真的回到了过去！',
              emotion: '震惊'
            },
            {
              character: '年轻时的爷爷',
              text: '小姐，你看起来很困惑，需要帮助吗？',
              emotion: '关切'
            }
          ]
        }
      ]
    },
    createdAt: '2025-10-01',
    views: 12580,
    likes: 892
  },
  {
    id: '2',
    title: '咖啡馆的邂逅',
    description: '都市爱情故事，关于缘分和选择',
    thumbnail: '/api/placeholder/800/450',
    duration: 150,
    genre: '爱情',
    tags: ['都市', '爱情', '缘分'],
    script: {
      scenes: [
        {
          id: 'scene1',
          title: '初次相遇',
          duration: 50,
          setting: '温馨的咖啡馆',
          dialogue: [
            {
              character: '小雅',
              text: '不好意思，这是你的座位吗？',
              emotion: '害羞'
            },
            {
              character: '李明',
              text: '没关系，我也可以坐对面。',
              emotion: '微笑'
            }
          ]
        }
      ]
    },
    createdAt: '2025-10-02',
    views: 8920,
    likes: 756
  }
];

const PlayerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [drama, setDrama] = useState<DramaScript | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [userChoice, setUserChoice] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 加载短剧数据
    const foundDrama = mockDramas.find(d => d.id === id);
    if (foundDrama) {
      setDrama(foundDrama);
      setLikeCount(foundDrama.likes);
    } else {
      navigate('/');
    }
  }, [id, navigate]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  // 模拟时间推进和场景切换
  useEffect(() => {
    if (isPlaying && drama) {
      const interval = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 1;
          if (newTime >= drama.duration) {
            setIsPlaying(false);
            return 0;
          }
          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isPlaying, drama?.duration]);

  // 根据时间自动切换场景
  useEffect(() => {
    if (drama) {
      let accumulatedTime = 0;
      let newSceneIndex = 0;

      for (let i = 0; i < drama.script.scenes.length; i++) {
        accumulatedTime += drama.script.scenes[i].duration;
        if (currentTime < accumulatedTime) {
          newSceneIndex = i;
          break;
        }
      }

      if (newSceneIndex !== currentSceneIndex) {
        setCurrentSceneIndex(newSceneIndex);
      }
    }
  }, [currentTime, drama?.script.scenes.length, currentSceneIndex]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && playerRef.current) {
      playerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChoice = (choiceId: string) => {
    setUserChoice(choiceId);

    // 模拟选择后的反馈和场景切换
    setTimeout(() => {
      if (drama) {
        // 如果是最后一个场景，循环回第一个场景
        if (currentSceneIndex >= drama.script.scenes.length - 1) {
          setCurrentSceneIndex(0);
        } else {
          setCurrentSceneIndex(prev => prev + 1);
        }
      }
      setUserChoice(null);
    }, 2000);
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: drama?.title,
        text: drama?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('链接已复制到剪贴板');
    }
  };

  if (!drama) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">加载中...</div>
      </div>
    );
  }

  const currentScene = drama.script.scenes[currentSceneIndex];
  const progress = (currentTime / drama.duration) * 100;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* 顶部导航 */}
      <div className="bg-gray-800/90 backdrop-blur-sm p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 hover:text-purple-400 transition">
            <span className="text-xl">←</span>
            <span>返回首页</span>
          </Link>
          <h1 className="text-xl font-bold">{drama.title}</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-1 ${isLiked ? 'text-red-500' : 'hover:text-red-500'} transition`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              <span>{likeCount}</span>
            </button>
            <button
              onClick={handleShare}
              className="hover:text-purple-400 transition"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* 主播放区域 */}
      <div ref={playerRef} className="relative bg-black">
        {/* 视频/内容区域 */}
        <div className="relative aspect-video">
          {/* 模拟视频播放器 */}
          <video
            ref={videoRef}
            className="w-full h-full"
            poster={drama.thumbnail}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => setIsPlaying(false)}
          >
            <source src="#" type="video/mp4" />
          </video>

          {/* 场景内容覆盖层 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-8 bg-black/50 rounded-lg max-w-4xl mx-4">
              <h2 className="text-2xl font-bold mb-4">{currentScene?.title}</h2>
              <p className="text-gray-300 mb-6">{currentScene?.setting}</p>

              {/* 对话内容 */}
              <div className="space-y-4 mb-8">
                {currentScene?.dialogue.map((dialogue, index) => (
                  <div key={index} className="text-left">
                    <p className="font-semibold text-purple-400">{dialogue.character}:</p>
                    <p className="text-lg">{dialogue.text}</p>
                  </div>
                ))}
              </div>

              {/* 互动选择 */}
              {currentScene?.dialogue[currentScene.dialogue.length - 1]?.choices && (
                <div className="space-y-3">
                  <p className="text-lg mb-4">你会如何回应？</p>
                  {currentScene.dialogue[currentScene.dialogue.length - 1].choices?.map((choice) => (
                    <button
                      key={choice.id}
                      onClick={() => handleChoice(choice.id)}
                      className="block w-full p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition text-left"
                    >
                      {choice.text}
                    </button>
                  ))}
                </div>
              )}

              {/* 用户选择反馈 */}
              {userChoice && (
                <div className="mt-6 p-4 bg-green-600/20 border border-green-400 rounded-lg">
                  <p className="text-green-400">你的选择已记录，故事将继续...</p>
                </div>
              )}
            </div>
          </div>

          {/* 播放控制条 */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            {/* 进度条 */}
            <div className="mb-4">
              <input
                type="range"
                min="0"
                max={drama.duration}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                style={{ background: `linear-gradient(to right, #9333ea ${progress}%, #4B5563 ${progress}%)` }}
              />
              <div className="flex justify-between text-sm mt-2">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(drama.duration)}</span>
              </div>
            </div>

            {/* 控制按钮 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setCurrentSceneIndex(Math.max(0, currentSceneIndex - 1))}
                  className="hover:text-purple-400 transition"
                >
                  <SkipBack className="w-5 h-5" />
                </button>

                <button
                  onClick={handlePlayPause}
                  className="bg-purple-600 hover:bg-purple-700 rounded-full p-3 transition"
                >
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>

                <button
                  onClick={() => setCurrentSceneIndex(Math.min(drama.script.scenes.length - 1, currentSceneIndex + 1))}
                  className="hover:text-purple-400 transition"
                >
                  <SkipForward className="w-5 h-5" />
                </button>

                <div className="flex items-center space-x-2">
                  <button onClick={toggleMute} className="hover:text-purple-400 transition">
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>

              <button
                onClick={toggleFullscreen}
                className="hover:text-purple-400 transition"
              >
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 互动区域 */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 剧情简介 */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">剧情简介</h2>
              <p className="text-gray-300 mb-4">{drama.description}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {drama.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>类型: {drama.genre}</span>
                <span>时长: {Math.floor(drama.duration / 60)}分钟</span>
                <span>{drama.views.toLocaleString()} 次观看</span>
              </div>
            </div>

            {/* 场景选择 */}
            <div className="bg-gray-800 rounded-lg p-6 mt-6">
              <h2 className="text-xl font-bold mb-4">场景选择</h2>
              <div className="space-y-3">
                {drama.script.scenes.map((scene, index) => (
                  <button
                    key={scene.id}
                    onClick={() => setCurrentSceneIndex(index)}
                    className={`w-full text-left p-4 rounded-lg transition ${
                      index === currentSceneIndex
                        ? 'bg-purple-600/30 border border-purple-500'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">场景 {index + 1}: {scene.title}</h3>
                        <p className="text-sm text-gray-400">{scene.setting}</p>
                      </div>
                      <span className="text-sm text-gray-400">{Math.floor(scene.duration / 60)}分钟</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 侧边栏 */}
          <div className="space-y-6">
            {/* 推荐短剧 */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">推荐短剧</h2>
              <div className="space-y-4">
                {mockDramas.filter(d => d.id !== drama.id).map((recDrama) => (
                  <Link
                    key={recDrama.id}
                    to={`/player/${recDrama.id}`}
                    className="block p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
                  >
                    <h3 className="font-semibold">{recDrama.title}</h3>
                    <p className="text-sm text-gray-400">{recDrama.genre}</p>
                    <p className="text-sm text-gray-400">{recDrama.views.toLocaleString()} 次观看</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* 互动统计 */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">互动统计</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>点赞数</span>
                  <span className="font-semibold">{likeCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>观看完成度</span>
                  <span className="font-semibold">{Math.round(progress)}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>当前场景</span>
                  <span className="font-semibold">{currentSceneIndex + 1}/{drama.script.scenes.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #9333ea;
          border-radius: 50%;
          cursor: pointer;
        }
        .slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #9333ea;
          border-radius: 50%;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default PlayerPage;